<template>
  <div id="test-management">

    <!-- Header -->
    <div class="test-management-header">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="text-primary mb-0">
            <feather-icon icon="CheckSquareIcon" class="mr-1"/>
            Test Management
          </h2>
          <p class="text-muted mb-0">Create and manage test packs and questions</p>
        </div>
        <div class="d-flex align-items-center">
          <b-button
              variant="outline-primary"
              class="mr-2"
              @click="refreshData"
              :disabled="isLoading"
          >
            <feather-icon icon="RefreshCwIcon" :class="{ 'spinner': isLoading }" size="16"/>
            Refresh
          </b-button>
          <b-button
              variant="primary"
              @click="openCreateTestPackModal"
          >
            <feather-icon icon="PlusIcon" size="16"/>
            Create Test Pack
          </b-button>
        </div>
      </div>
    </div>

    <!-- Analytics Cards -->
    <div class="analytics-section mb-4">
      <b-row>
        <b-col lg="3" md="6" sm="6">
          <b-card class="analytics-card">
            <div class="d-flex align-items-center">
              <div class="analytics-icon bg-primary">
                <feather-icon icon="PackageIcon" class="text-white"/>
              </div>
              <div class="analytics-content">
                <h3 class="analytics-number text-primary">{{ analytics.overview.total_test_packs || 0 }}</h3>
                <p class="analytics-text mb-0">Total Test Packs</p>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col lg="3" md="6" sm="6">
          <b-card class="analytics-card">
            <div class="d-flex align-items-center">
              <div class="analytics-icon bg-success">
                <feather-icon icon="HelpCircleIcon" class="text-white"/>
              </div>
              <div class="analytics-content">
                <h3 class="analytics-number text-success">{{ analytics.overview.total_questions || 0 }}</h3>
                <p class="analytics-text mb-0">Total Questions</p>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col lg="3" md="6" sm="6">
          <b-card class="analytics-card">
            <div class="d-flex align-items-center">
              <div class="analytics-icon bg-warning">
                <feather-icon icon="PlayIcon" class="text-white"/>
              </div>
              <div class="analytics-content">
                <h3 class="analytics-number text-warning">{{ analytics.overview.active_test_packs || 0 }}</h3>
                <p class="analytics-text mb-0">Active Test Packs</p>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col lg="3" md="6" sm="6">
          <b-card class="analytics-card">
            <div class="d-flex align-items-center">
              <div class="analytics-icon bg-info">
                <feather-icon icon="BarChartIcon" class="text-white"/>
              </div>
              <div class="analytics-content">
                <h3 class="analytics-number text-info">{{ averageQuestionsPerPack }}</h3>
                <p class="analytics-text mb-0">Avg Questions/Pack</p>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Tabs Navigation -->
    <b-card no-body class="test-management-card">
      <b-tabs card>

        <!-- Test Packs Tab -->
        <b-tab title="Test Packs" active>
          <template #title>
            <feather-icon icon="PackageIcon" size="16" class="mr-1"/>
            Test Packs ({{ testPacks.length }})
          </template>

          <!-- Filters -->
          <div class="filters-section mb-4">
            <b-row>
              <b-col lg="3" md="6" sm="12">
                <b-form-group label="Search" class="mb-2">
                  <b-input-group>
                    <b-form-input
                        v-model="filters.search"
                        placeholder="Search test packs..."
                        @input="onFiltersChange"
                    />
                    <b-input-group-append>
                      <b-button variant="outline-secondary" @click="filters.search = ''; onFiltersChange()">
                        <feather-icon icon="XIcon" size="14"/>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col lg="3" md="6" sm="12">
                <b-form-group label="Subject" class="mb-2">
                  <v-select
                      v-model="filters.subject_id"
                      :options="subjects"
                      label="name"
                      :reduce="subject => subject.id"
                      placeholder="All Subjects"
                      :clearable="true"
                      @input="onFiltersChange"
                  />
                </b-form-group>
              </b-col>
              <b-col lg="3" md="6" sm="12">
                <b-form-group label="Difficulty" class="mb-2">
                  <v-select
                      v-model="filters.difficulty"
                      :options="difficultyOptions"
                      placeholder="All Difficulties"
                      :clearable="true"
                      @input="onFiltersChange"
                  />
                </b-form-group>
              </b-col>
              <b-col lg="3" md="6" sm="12">
                <b-form-group label="Status" class="mb-2">
                  <v-select
                      v-model="filters.status"
                      :options="statusOptions"
                      placeholder="All Statuses"
                      :clearable="true"
                      @input="onFiltersChange"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Test Packs Table -->
          <div class="table-responsive">
            <vue-good-table
                :columns="testPackColumns"
                :rows="filteredTestPacks"
                :pagination-options="{ enabled: true, perPage: 10 }"
                :search-options="{ enabled: false }"
                :sort-options="{ enabled: true }"
                styleClass="vgt-table striped"
                :loading="isLoading"
                :empty-state="{ enabled: true, text: 'No test packs found' }"
            >
              <template slot="loadingContent">
                <div class="d-flex justify-content-center p-4">
                  <b-spinner variant="primary"></b-spinner>
                </div>
              </template>

              <template slot="table-row" slot-scope="props">
                <!-- Name Column -->
                <div v-if="props.column.field === 'name'" class="test-pack-name">
                  <div class="d-flex align-items-center">
                    <div class="test-pack-icon mr-2">
                      <feather-icon
                        :icon="getTestPackIcon(props.row.difficulty_level)"
                        :class="getDifficultyClass(props.row.difficulty_level)"
                        size="20"
                      />
                    </div>
                    <div>
                      <h6 class="mb-0">{{ props.row.name }}</h6>
                      <small class="text-muted" v-if="props.row.description">
                        {{ props.row.description | truncate(50) }}
                      </small>
                    </div>
                  </div>
                </div>

                <!-- Subject Column -->
                <div v-else-if="props.column.field === 'subject'" class="subject-info">
                  <b-badge variant="outline-info" v-if="props.row.subject">
                    {{ props.row.subject.name }}
                  </b-badge>
                  <span v-else class="text-muted">No Subject</span>
                </div>

                <!-- Difficulty Column -->
                <div v-else-if="props.column.field === 'difficulty_level'" class="difficulty-badge">
                  <b-badge :variant="getDifficultyVariant(props.row.difficulty_level)">
                    {{ props.row.difficulty_level | capitalize }}
                  </b-badge>
                </div>

                <!-- Questions Count Column -->
                <div v-else-if="props.column.field === 'questions_count'" class="questions-count">
                  <div class="d-flex align-items-center">
                    <feather-icon icon="HelpCircleIcon" size="14" class="mr-1 text-muted"/>
                    <span>{{ (props.row.questions || []).length }}</span>
                    <small class="text-muted ml-1">questions</small>
                  </div>
                </div>

                <!-- Total Points Column -->
                <div v-else-if="props.column.field === 'total_points'" class="total-points">
                  <div class="d-flex align-items-center">
                    <feather-icon icon="AwardIcon" size="14" class="mr-1 text-muted"/>
                    <span>{{ props.row.total_points || 0 }}</span>
                    <small class="text-muted ml-1">pts</small>
                  </div>
                </div>

                <!-- Status Column -->
                <div v-else-if="props.column.field === 'status'" class="status-badge">
                  <b-badge :variant="getStatusVariant(props.row.status)">
                    <feather-icon :icon="getStatusIcon(props.row.status)" size="12" class="mr-1"/>
                    {{ props.row.status | capitalize }}
                  </b-badge>
                </div>

                <!-- Created At Column -->
                <div v-else-if="props.column.field === 'created_at'" class="created-date">
                  <small class="text-muted">{{ props.row.created_at | formatDate }}</small>
                </div>

                <!-- Actions Column -->
                <div v-else-if="props.column.field === 'actions'" class="actions">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="viewTestPack(props.row)"
                  >
                    <feather-icon icon="EyeIcon" size="14" class="mr-1"/>
                    View
                  </b-button>
                </div>

                <!-- Default Column -->
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </b-tab>

        <!-- Questions Management Tab -->
        <b-tab :title="`Questions ${selectedTestPack ? `(${selectedTestPack.name})` : ''}`" :disabled="!selectedTestPack">
          <template #title>
            <feather-icon icon="HelpCircleIcon" size="16" class="mr-1"/>
            Questions {{ selectedTestPack ? `(${selectedTestPack.name})` : '' }}
          </template>

          <div v-if="selectedTestPack">
            <QuestionsManager
              :test-pack="selectedTestPack"
              @question-created="onQuestionCreated"
              @question-updated="onQuestionUpdated"
              @question-deleted="onQuestionDeleted"
              @back-to-test-packs="selectedTestPack = null"
            />
          </div>
          <div v-else class="text-center p-4">
            <feather-icon icon="HelpCircleIcon" size="48" class="text-muted mb-3"/>
            <h5 class="text-muted">Select a test pack to manage questions</h5>
            <p class="text-muted">Choose a test pack from the "Test Packs" tab to start managing its questions.</p>
          </div>
        </b-tab>

        <!-- Analytics Tab -->
        <b-tab title="Analytics">
          <template #title>
            <feather-icon icon="BarChartIcon" size="16" class="mr-1"/>
            Analytics
          </template>

          <TestAnalytics
            :analytics="analytics"
            @refresh="loadAnalytics"
          />
        </b-tab>

        <!-- Import/Export Tab -->
        <b-tab title="Import/Export">
          <template #title>
            <feather-icon icon="UploadIcon" size="16" class="mr-1"/>
            Import/Export
          </template>

          <ImportExportManager
            :test-packs="testPacks"
            @questions-imported="onQuestionsImported"
          />
        </b-tab>

      </b-tabs>
    </b-card>

    <!-- Test Pack Create/Edit Modal -->
    <TestPackModal
        :visible="showTestPackModal"
        :test-pack="editingTestPack"
        :subjects="subjects"
        @save="onTestPackSaved"
        @close="closeTestPackModal"
    />

    <!-- Question Modal -->
    <QuestionModal
        :visible="showQuestionModal"
        :question="editingQuestion"
        :test-pack="selectedTestPack"
        @save="onQuestionSaved"
        @close="closeQuestionModal"
    />

  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import {
  BButton,
  BCard,
  BTabs,
  BTab,
  BRow,
  BCol,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BFormInput,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BSpinner
} from 'bootstrap-vue'
import vSelect from 'vue-select'

// Import custom components (we'll create these)
import TestPackModal from './components/TestPackModal.vue'
import QuestionModal from './components/QuestionModal.vue'
import QuestionsManager from './components/QuestionsManager.vue'
import TestAnalytics from './components/TestAnalytics.vue'
import ImportExportManager from './components/ImportExportManager.vue'

// Import API
import TestManagementApi from '@/api/testManagement'

export default {
  name: 'TestManagement',
  components: {
    VueGoodTable,
    BButton,
    BCard,
    BTabs,
    BTab,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BSpinner,
    vSelect,
    TestPackModal,
    QuestionModal,
    QuestionsManager,
    TestAnalytics,
    ImportExportManager
  },

  data() {
    return {
      // Loading state
      isLoading: false,

      // Data
      testPacks: [],
      subjects: [],
      analytics: {
        overview: {},
        charts: {}
      },

      // Filters
      filters: {
        search: '',
        subject_id: null,
        difficulty: null,
        status: null
      },

      // UI State
      showTestPackModal: false,
      showQuestionModal: false,
      editingTestPack: null,
      editingQuestion: null,
      selectedTestPack: null,

      // Options
      difficultyOptions: [
        { label: 'Beginner', value: 'beginner' },
        { label: 'Intermediate', value: 'intermediate' },
        { label: 'Advanced', value: 'advanced' }
      ],

      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Archived', value: 'archived' }
      ],

      // Table columns
      testPackColumns: [
        {
          label: 'Test Pack',
          field: 'name',
          sortable: true,
          thClass: 'text-left',
          tdClass: 'text-left'
        },
        {
          label: 'Subject',
          field: 'subject',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          label: 'Difficulty',
          field: 'difficulty_level',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          label: 'Questions',
          field: 'questions_count',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          label: 'Points',
          field: 'total_points',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          label: 'Status',
          field: 'status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          label: 'Created',
          field: 'created_at',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center'
        }
      ]
    }
  },

  computed: {
    filteredTestPacks() {
      return this.testPacks.filter(testPack => {
        // Search filter
        if (this.filters.search) {
          const search = this.filters.search.toLowerCase()
          if (!testPack.name?.toLowerCase().includes(search) &&
              !testPack.description?.toLowerCase().includes(search)) {
            return false
          }
        }

        // Subject filter
        if (this.filters.subject_id && testPack.subject?.id !== this.filters.subject_id) {
          return false
        }

        // Difficulty filter
        if (this.filters.difficulty && testPack.difficulty_level !== this.filters.difficulty) {
          return false
        }

        // Status filter
        if (this.filters.status && testPack.status !== this.filters.status) {
          return false
        }

        return true
      })
    },

    averageQuestionsPerPack() {
      if (this.testPacks.length === 0) return 0
      const totalQuestions = this.testPacks.reduce((sum, pack) => sum + (pack.questions?.length || 0), 0)
      return Math.round(totalQuestions / this.testPacks.length * 10) / 10
    }
  },

  filters: {
    capitalize(value) {
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1)
    },

    truncate(value, length = 50) {
      if (!value) return ''
      return value.length > length ? value.substring(0, length) + '...' : value
    },

    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString()
    }
  },

  mounted() {
    this.init()
  },

  methods: {
    // Initialization
    async init() {
      await Promise.all([
        this.loadTestPacks(),
        this.loadSubjects(),
        this.loadAnalytics()
      ])
    },

    async refreshData() {
      this.isLoading = true
      try {
        await Promise.all([
          this.loadTestPacks(),
          this.loadAnalytics()
        ])
      } finally {
        this.isLoading = false
      }
    },

    // Data Loading
    async loadTestPacks() {
      try {
        this.isLoading = true
        const api = new TestManagementApi()
        const response = await api.getTestPacks(this.filters)
        this.testPacks = response.data.data || response.data || []
      } catch (error) {
        console.error('Error loading test packs:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to load test packs',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    async loadSubjects() {
      try {
        const api = new TestManagementApi()
        const response = await api.getSubjects()
        this.subjects = response.data || []
      } catch (error) {
        console.error('Error loading subjects:', error)
      }
    },

    async loadAnalytics() {
      try {
        const api = new TestManagementApi()
        const response = await api.getAnalytics()
        this.analytics = response.data || { overview: {}, charts: {} }
      } catch (error) {
        console.error('Error loading analytics:', error)
      }
    },

    // Filter handling
    onFiltersChange() {
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => {
        this.loadTestPacks()
      }, 500)
    },

    // Modal handling
    openCreateTestPackModal() {
      this.editingTestPack = null
      this.showTestPackModal = true
    },

    closeTestPackModal() {
      this.showTestPackModal = false
      this.editingTestPack = null
    },

    closeQuestionModal() {
      this.showQuestionModal = false
      this.editingQuestion = null
    },

    // Test Pack Actions
    viewTestPack(testPack) {
      this.$router.push(`/admin/tests/${testPack.id}`)
    },

    editTestPack(testPack) {
      this.editingTestPack = testPack
      this.showTestPackModal = true
    },

    manageQuestions(testPack) {
      this.selectedTestPack = testPack
      // Switch to questions tab
      this.$nextTick(() => {
        const tabsComponent = this.$children.find(c => c.$options.name === 'BTabs')
        if (tabsComponent) {
          tabsComponent.activeTabIndex = 1
        }
      })
    },

    async duplicateTestPack(testPack) {
      try {
        this.isLoading = true
        const api = new TestManagementApi()
        await api.duplicateTestPack(testPack.id)

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: `Test pack "${testPack.name}" duplicated successfully`,
            variant: 'success',
          },
        })

        await this.loadTestPacks()
      } catch (error) {
        console.error('Error duplicating test pack:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to duplicate test pack',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    async exportTestPack(testPack) {
      try {
        const api = new TestManagementApi()
        const response = await api.exportQuestions(testPack.id)

        // Download the JSON file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${testPack.name.replace(/[^a-zA-Z0-9]/g, '_')}_questions.json`
        link.click()
        window.URL.revokeObjectURL(url)

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'DownloadIcon',
            text: 'Questions exported successfully',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error exporting test pack:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to export questions',
            variant: 'danger',
          },
        })
      }
    },

    async toggleTestPackStatus(testPack) {
      try {
        const newStatus = testPack.status === 'active' ? 'inactive' : 'active'
        const api = new TestManagementApi()
        await api.updateTestPack(testPack.id, { ...testPack, status: newStatus })

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: `Test pack ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
            variant: 'success',
          },
        })

        await this.loadTestPacks()
      } catch (error) {
        console.error('Error updating test pack status:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to update test pack status',
            variant: 'danger',
          },
        })
      }
    },

    async deleteTestPack(testPack) {
      try {
        const result = await this.$bvModal.msgBoxConfirm(
          `Are you sure you want to delete the test pack "${testPack.name}"? This action cannot be undone.`,
          {
            title: 'Confirm Deletion',
            size: 'md',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel'
          }
        )

        if (result) {
          const api = new TestManagementApi()
          await api.deleteTestPack(testPack.id)

          this.$toast({
            component: 'ToastificationContent',
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: 'Test pack deleted successfully',
              variant: 'success',
            },
          })

          await this.loadTestPacks()
        }
      } catch (error) {
        console.error('Error deleting test pack:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.error || 'Failed to delete test pack',
            variant: 'danger',
          },
        })
      }
    },

    // Event Handlers
    async onTestPackSaved(testPack) {
      this.closeTestPackModal()
      await this.loadTestPacks()
      await this.loadAnalytics()
    },

    async onQuestionSaved(question) {
      this.closeQuestionModal()
      // Refresh the selected test pack
      if (this.selectedTestPack) {
        const api = new TestManagementApi()
        const response = await api.getTestPackQuestions(this.selectedTestPack.id)
        this.selectedTestPack = response.data.test_pack
      }
      await this.loadAnalytics()
    },

    onQuestionCreated(question) {
      this.onQuestionSaved(question)
    },

    onQuestionUpdated(question) {
      this.onQuestionSaved(question)
    },

    onQuestionDeleted() {
      this.onQuestionSaved()
    },

    async onQuestionsImported(result) {
      await this.loadTestPacks()
      await this.loadAnalytics()
    },

    // Helper methods
    getTestPackIcon(difficulty) {
      switch (difficulty) {
        case 'beginner': return 'BookIcon'
        case 'intermediate': return 'BookOpenIcon'
        case 'advanced': return 'AwardIcon'
        default: return 'HelpCircleIcon'
      }
    },

    getDifficultyClass(difficulty) {
      switch (difficulty) {
        case 'beginner': return 'text-success'
        case 'intermediate': return 'text-warning'
        case 'advanced': return 'text-danger'
        default: return 'text-muted'
      }
    },

    getDifficultyVariant(difficulty) {
      switch (difficulty) {
        case 'beginner': return 'light-success'
        case 'intermediate': return 'light-warning'
        case 'advanced': return 'light-danger'
        default: return 'light-secondary'
      }
    },

    getStatusVariant(status) {
      switch (status) {
        case 'active': return 'light-success'
        case 'inactive': return 'light-warning'
        case 'archived': return 'light-secondary'
        default: return 'light-secondary'
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 'active': return 'PlayIcon'
        case 'inactive': return 'PauseIcon'
        case 'archived': return 'ArchiveIcon'
        default: return 'HelpCircleIcon'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#test-management {
  .test-management-header {
    background: linear-gradient(118deg, #7367F0, rgba(115, 103, 240, 0.7));
    border-radius: 0.5rem;
    padding: 2rem;
    margin-bottom: 2rem;
    color: white;

    h2 {
      color: white !important;
    }
  }

  .analytics-section {
    .analytics-card {
      border: none;
      box-shadow: 0 4px 25px 0 rgba(0,0,0,.1);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 25px 0 rgba(0,0,0,.15);
      }

      .analytics-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
      }

      .analytics-number {
        font-size: 2rem;
        font-weight: 700;
        margin-bottom: 0;
      }

      .analytics-text {
        font-size: 0.9rem;
        opacity: 0.7;
      }
    }
  }

  .test-management-card {
    box-shadow: 0 4px 25px 0 rgba(0,0,0,.1);

    .card-header {
      background: linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #dee2e6;
    }
  }

  .filters-section {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 0.5rem;
    border-left: 4px solid #7367F0;
  }

  .test-pack-name {
    .test-pack-icon {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      background: #f8f9fa;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h6 {
      font-weight: 600;
      color: #2c2c2c;
    }
  }

  .subject-info .badge {
    font-size: 0.75rem;
  }

  .difficulty-badge .badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }

  .status-badge .badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    display: inline-flex;
    align-items: center;
  }

  .questions-count,
  .total-points {
    font-weight: 500;
  }

  .actions .dropdown-toggle {
    border: none;
    background: none;
    box-shadow: none;

    &:focus {
      box-shadow: none;
    }
  }
}

// Animation for loading spinner
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Vue Good Table customizations
::v-deep .vgt-table {
  .vgt-table-header {
    background: #f8f9fa;

    th {
      background: #f8f9fa !important;
      border-color: #dee2e6 !important;
      font-weight: 600;
      color: #2c2c2c;
    }
  }

  tbody tr {
    &:hover {
      background: #f8f9fa !important;
    }
  }
}

// Vue Select customizations
::v-deep .v-select {
  .vs__dropdown-toggle {
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #7367F0;
    box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
  }

  .vs__selected {
    color: #2c2c2c;
  }
}
</style>